import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Zap, Plus, Trash2, CheckCircle2, Sliders, ToggleLeft, ToggleRight, ArrowRight } from 'lucide-react'
import { useSpace } from '../hooks'
import { cn } from '../utils'
import { cardClassName } from '@jira-clone/ui'

interface Rule {
  id: string
  name: string
  trigger: string
  action: string
  enabled: boolean
  runsCount: number
}

const DEFAULT_RULES: Rule[] = [
  {
    id: 'rule-1',
    name: 'Auto-close issues when moved to Done',
    trigger: 'When issue status moves to "Done"',
    action: 'Set resolution to "Completed" and record closed timestamp',
    enabled: true,
    runsCount: 24,
  },
  {
    id: 'rule-2',
    name: 'Assign newly created bugs to QA Lead',
    trigger: 'When issue type is "Bug" and created',
    action: 'Assign issue to QA Lead and flag high priority',
    enabled: true,
    runsCount: 8,
  },
  {
    id: 'rule-3',
    name: 'Notify channel on Critical blocker',
    trigger: 'When priority is set to "Critical"',
    action: 'Post alert notification to project channel',
    enabled: false,
    runsCount: 3,
  },
  {
    id: 'rule-4',
    name: 'Sync Pull Request branch name with Issue Key',
    trigger: 'When new branch created matching TASK-*',
    action: 'Link GitHub branch and move issue to In Progress',
    enabled: true,
    runsCount: 15,
  },
]

export function AutomationPage() {
  const params = useParams<{ id?: string; spaceId?: string }>()
  const spaceId = params.id || params.spaceId || ''
  const { data: space } = useSpace(spaceId ?? null)

  const storageKey = `goodday_space_rules_${spaceId || 'default'}`

  const [rules, setRules] = useState<Rule[]>(() => {
    if (typeof window !== 'undefined' && spaceId) {
      try {
        const saved = localStorage.getItem(`goodday_space_rules_${spaceId}`)
        if (saved) return JSON.parse(saved)
      } catch {
        // fallback
      }
    }
    return DEFAULT_RULES
  })

  useEffect(() => {
    if (typeof window !== 'undefined' && spaceId) {
      try {
        const saved = localStorage.getItem(storageKey)
        if (saved) {
          setRules(JSON.parse(saved))
        } else {
          setRules(DEFAULT_RULES)
        }
      } catch {
        // fallback
      }
    }
  }, [spaceId, storageKey])

  const persistRules = (newRules: Rule[]) => {
    setRules(newRules)
    if (typeof window !== 'undefined' && spaceId) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(newRules))
      } catch {
        // ignore
      }
    }
  }

  const [modalOpen, setModalOpen] = useState(false)
  const [ruleName, setRuleName] = useState('')
  const [trigger, setTrigger] = useState('When issue status changes')
  const [action, setAction] = useState('Assign issue to current user')

  const toggleRule = (id: string) => {
    const next = rules.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r))
    persistRules(next)
  }

  const deleteRule = (id: string) => {
    const next = rules.filter((r) => r.id !== id)
    persistRules(next)
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!ruleName.trim()) return
    const newRule: Rule = {
      id: `rule-${Date.now()}`,
      name: ruleName.trim(),
      trigger,
      action,
      enabled: true,
      runsCount: 0,
    }
    persistRules([newRule, ...rules])
    setRuleName('')
    setModalOpen(false)
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto p-6 bg-white dark:bg-slate-950 space-y-6 font-sans">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Space Automations — {space?.name || 'Space'}
          </h2>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Automate routine tasks, status transitions, notifications, and assignments
          </p>
        </div>

        <button
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-blue-700"
        >
          <Plus className="h-3.5 w-3.5" /> Create Rule
        </button>
      </div>

      {/* Rules List */}
      <div className="space-y-3">
        {rules.map((rule) => (
          <div
            key={rule.id}
            className={cn(cardClassName(), 'flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4')}
          >
            <div className="flex items-start gap-3">
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg mt-0.5',
                  rule.enabled
                    ? 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400'
                    : 'bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500',
                )}
              >
                <Zap className="h-4 w-4" />
              </span>

              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100">{rule.name}</h3>
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                      rule.enabled
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400',
                    )}
                  >
                    {rule.enabled ? 'Active' : 'Disabled'}
                  </span>
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-600 dark:text-slate-300">
                  <span className="font-semibold text-gray-800 dark:text-slate-200">{rule.trigger}</span>
                  <ArrowRight className="h-3 w-3 text-gray-400" />
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">{rule.action}</span>
                </div>

                <p className="mt-1 text-[11px] text-gray-400">Executed {rule.runsCount} times</p>
              </div>
            </div>

            <div className="flex items-center gap-2 self-end sm:self-center">
              <button
                onClick={() => toggleRule(rule.id)}
                className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                {rule.enabled ? (
                  <>
                    <ToggleRight className="h-4 w-4 text-emerald-600" /> Enabled
                  </>
                ) : (
                  <>
                    <ToggleLeft className="h-4 w-4 text-gray-400" /> Disabled
                  </>
                )}
              </button>

              <button
                onClick={() => deleteRule(rule.id)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-slate-800"
                title="Delete rule"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}

        {rules.length === 0 && (
          <div className="py-12 text-center">
            <Zap className="h-8 w-8 text-gray-300 dark:text-slate-700 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-500">No automation rules configured</p>
            <p className="text-xs text-gray-400 mt-1">Create rules to automate repetitive tasks in this space.</p>
          </div>
        )}
      </div>

      {/* New Rule Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900 border border-gray-200 dark:border-slate-800">
            <h3 className="text-base font-bold text-gray-900 dark:text-slate-100">Create Automation Rule</h3>
            <form onSubmit={handleCreate} className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                  Rule Name
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  placeholder="e.g. Notify team on priority change"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                  When (Trigger)
                </label>
                <select
                  value={trigger}
                  onChange={(e) => setTrigger(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="When issue status moves to 'Done'">When issue status moves to &quot;Done&quot;</option>
                  <option value="When issue type is 'Bug' and created">When issue type is &quot;Bug&quot; and created</option>
                  <option value="When priority is set to 'Critical'">When priority is set to &quot;Critical&quot;</option>
                  <option value="When due date is within 24 hours">When due date is within 24 hours</option>
                  <option value="When issue is assigned">When issue is assigned</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                  Then (Action)
                </label>
                <select
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Set resolution to 'Completed'">Set resolution to &quot;Completed&quot;</option>
                  <option value="Assign issue to QA Lead">Assign issue to QA Lead</option>
                  <option value="Post alert notification to project channel">Post alert notification to project channel</option>
                  <option value="Send email alert to assignee">Send email alert to assignee</option>
                  <option value="Move issue to Next Sprint">Move issue to Next Sprint</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 shadow-xs"
                >
                  Create Rule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
